
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Mail, Lock, Shield, AlertCircle } from "lucide-react";

export default function AdminSignupPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isExistingUser, setIsExistingUser] = useState(false);
  const navigate = useNavigate();

  const handleExistingUserAdmin = async () => {
    setIsLoading(true);
    
    try {
      // Call RPC function to make existing user an admin
      const { error: rpcError } = await supabase.rpc('create_first_admin', {
        admin_email: email
      });

      if (rpcError) {
        console.error("RPC Error:", rpcError);
        throw rpcError;
      }

      console.log("Admin role assigned successfully to existing user");
      toast.success("Admin privileges granted to existing user. You can now login with your credentials.");
      
      // Redirect to auth page
      setTimeout(() => {
        navigate("/auth");
      }, 2000);
    } catch (error: any) {
      console.error("Error assigning admin role:", error);
      toast.error(error.message || "Failed to assign admin role");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      toast.error("Password must be at least 6 characters long");
      return;
    }

    setIsLoading(true);

    try {
      // First, create the user with supabase auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
      });

      if (authError) {
        console.error("Auth Error:", authError);
        
        // Check if the error is because the user already exists
        if (authError.message?.includes("Email already registered")) {
          setIsExistingUser(true);
          setIsLoading(false);
          return;
        }
        
        throw authError;
      }

      if (!authData.user) {
        throw new Error("Failed to create user");
      }

      console.log("User created successfully with ID:", authData.user.id);

      // Now call the create_first_admin RPC function to make this user an admin
      const { error: rpcError } = await supabase.rpc('create_first_admin', {
        admin_email: email
      });

      if (rpcError) {
        console.error("RPC Error:", rpcError);
        throw rpcError;
      }

      console.log("Admin role assigned successfully");
      toast.success("Admin account created successfully! You can now login with your credentials.");
      
      // After successful creation, redirect to auth page
      setTimeout(() => {
        navigate("/auth");
      }, 2000);
    } catch (error: any) {
      console.error("Error creating admin:", error);
      
      // Provide more specific error messages based on the error type
      if (error.message?.includes("Email already registered")) {
        toast.error("This email is already registered. Please use a different email or try logging in.");
      } else if (error.message?.includes("rate limit")) {
        toast.error("Too many requests. Please try again later.");
      } else {
        toast.error(error.message || "Failed to create admin account");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="relative pt-24 pb-16 flex-1 flex flex-col items-center justify-center px-6">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute left-1/2 top-0 h-[500px] w-[1000px] -translate-x-1/2 bg-gradient-to-br from-primary/20 via-blue-50 to-background blur-3xl"></div>
        </div>
        
        <div className="relative w-full max-w-md">
          <div className="bg-white shadow-xl rounded-2xl p-8 mb-10">
            <div className="text-center mb-8">
              <Shield className="h-12 w-12 mx-auto text-primary mb-2" />
              <h1 className="text-3xl font-bold font-display tracking-tight">
                Create Admin Account
              </h1>
              <p className="text-muted-foreground mt-2">
                This page is for creating the first admin account
              </p>
            </div>
            
            {isExistingUser ? (
              <div className="space-y-6">
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start">
                    <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5 mr-2" />
                    <div>
                      <h3 className="font-medium text-amber-800">User already exists</h3>
                      <p className="text-sm text-amber-700 mt-1">
                        The email <span className="font-medium">{email}</span> is already registered. 
                        Would you like to grant admin privileges to this existing user?
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setIsExistingUser(false)}
                  >
                    Go Back
                  </Button>
                  <Button
                    onClick={handleExistingUserAdmin}
                    className="flex-1"
                    disabled={isLoading}
                  >
                    {isLoading ? "Processing..." : "Grant Admin Access"}
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="flex gap-1 items-center">
                    <Mail className="h-4 w-4" /> Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="admin@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-11"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password" className="flex gap-1 items-center">
                    <Lock className="h-4 w-4" /> Password
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-11"
                  />
                  <p className="text-xs text-muted-foreground">
                    Password must be at least 6 characters long
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="flex gap-1 items-center">
                    <Lock className="h-4 w-4" /> Confirm Password
                  </Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="h-11"
                  />
                </div>
                
                <Button
                  type="submit"
                  className="w-full h-11 text-base"
                  disabled={isLoading}
                >
                  {isLoading ? "Creating Admin..." : "Create Admin Account"}
                </Button>
              </form>
            )}
            
            <div className="mt-6 text-center">
              <Button
                variant="ghost"
                onClick={() => navigate("/auth")}
                className="text-primary underline underline-offset-4 text-sm"
              >
                Back to Login
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
